import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Check, ChevronRight, HelpCircle } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useProfile } from "../../hooks/queries";
import { useChatWidget } from "../ChatWidget";
import { UserAvatar } from "../ui/user-avatar";
import { SectionLabel } from "../ui/section-label";
import { TOURS, pageTours } from "../../lib/tours";
import { useHelp } from "./HelpProvider";
import { cn } from "@/lib/utils";

/** The ? button with the Hulp menu (HMPs only), next to the Meldingen/Taken buttons. */
export default function HelpButton() {
  const { enabled, menuOpen, setMenuOpen, startTour, completed } = useHelp();
  const { user } = useAuth();
  const { data: profile } = useProfile(enabled);
  const { openAlgemeenChat } = useChatWidget();
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen, setMenuOpen]);

  if (!enabled) return null;

  const onPage = pageTours(location.pathname, `/vacatures/${user?.partner_id ?? ""}`);
  const rest = TOURS.map(t => t.id).filter(id => !onPage.includes(id));
  const contact = profile?.waves_contacts?.[0]?.name;

  const topic = (id: string) => {
    const tour = TOURS.find(t => t.id === id)!;
    const done = completed.includes(id);
    return (
      <button
        key={id}
        type="button"
        role="menuitem"
        onClick={() => startTour(id)}
        className="flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-left text-sm font-medium text-waves-ink transition-colors hover:bg-waves-subtle"
      >
        <span className="flex-1">{tour.label}</span>
        {done ? (
          <span title="Afgerond" className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-status-ok-bg text-status-ok-fg">
            <Check size={12} strokeWidth={3} />
          </span>
        ) : (
          <ChevronRight size={15} className="shrink-0 text-waves-muted" />
        )}
      </button>
    );
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-tour="help"
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Hulp"
        title="Hulp"
        aria-expanded={menuOpen}
        className={cn(
          "flex h-11 w-11 items-center justify-center rounded-pill border border-waves-line text-waves-ink shadow-soft transition-colors hover:bg-waves-mist",
          menuOpen ? "bg-waves-mist" : "bg-white",
        )}
      >
        <HelpCircle size={20} />
      </button>

      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 flex max-h-[calc(100vh-96px)] w-[calc(100vw-2rem)] flex-col gap-1 overflow-y-auto rounded-card border border-waves-line bg-white p-3 shadow-pop md:w-[340px]"
        >
          <div className="flex flex-col gap-0.5 px-3 pb-2.5 pt-2">
            <div className="text-[17px] font-black tracking-[-0.01em]">Hulp</div>
            <div className="text-[13px] text-waves-muted">Kies een onderwerp. We lopen het stap voor stap met je door.</div>
          </div>
          {onPage.length > 0 && (
            <div className="flex flex-col gap-0.5 pb-1.5">
              <SectionLabel className="px-3 pb-1 pt-1.5">Op deze pagina</SectionLabel>
              {onPage.map(topic)}
            </div>
          )}
          <div className="flex flex-col gap-0.5 border-t border-waves-line pt-2">
            <SectionLabel className="px-3 pb-1 pt-1.5">{onPage.length > 0 ? "Alle onderwerpen" : "Onderwerpen"}</SectionLabel>
            {rest.map(topic)}
          </div>
          <button
            type="button"
            onClick={() => { setMenuOpen(false); openAlgemeenChat(); }}
            className="mt-1.5 flex items-center gap-3 border-t border-waves-line px-3 pb-1.5 pt-3.5 text-left"
          >
            <UserAvatar name={contact ?? "Waves"} size={32} />
            <span className="flex-1 text-[13px] leading-snug text-waves-body">
              Kom je er niet uit? <strong className="text-waves-accent">Vraag het {contact?.split(" ")[0] ?? "Waves"}</strong>
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
